import { Injectable } from '@angular/core';
import { MessageTrayData } from '../message-tray/message-tray.data';
import * as uuid from 'uuid';
import { MessageTrayService } from '../message-tray/message-tray.service';
import { Subscription, timer } from 'rxjs';
import { AuditData } from '../message-tray/audit-data';
import { FhirService } from '../patient/fhir.service';
import { AppStateService } from '../shared/service/app.state.service';
import * as fhir from 'fhir/r4';
import { AngularAppLogger } from '../angular-app-logger';

@Injectable({
  providedIn: 'root'
})
export class AuditEventService {

  infoQueue: MessageTrayData[] = [];
  errorQueue: MessageTrayData[] = [];
  sessionId!: string;
  auditEventInProgress!: boolean;
  appName!: string;
  timerSubscription!: Subscription;
  isInitialized!: boolean | false;

  constructor(readonly mTrayService: MessageTrayService,
              readonly fhirService: FhirService,
              readonly appStateService: AppStateService,
              readonly appLogger: AngularAppLogger) {
    this.appStateService.appLabel$.subscribe(label => {
      this.appName = label ?? '';
      // Do not create subscription again if already initialized.
      if (!this.isInitialized && localStorage.getItem('fhir_version') === 'R4' && this.appName === 'Reporting Application') {
        this.isInitialized = true;
        this.createSessionId();
        this.subscribeToMessageTray();
        this.startTimer();
        this.checkBrowserClose();
      } else if (this.appName) {
          this.appLogger.logInfo(`AuditEventService is only enabled in FHIR R4 onwards`, { context: `AuditEventService constructor log`});
      }
    });
  }

  createSessionId() {
    const storedSessionId = sessionStorage.getItem('auditSessionId');
    this.sessionId = storedSessionId || uuid.v4();
    if (!storedSessionId)
      sessionStorage.setItem('auditSessionId', this.sessionId);
  }

  subscribeToMessageTray() {
    this.mTrayService.getMessage().subscribe(msg => {
      const stringifiedMsg: MessageTrayData = {
        ...msg,
        message: typeof msg.message === 'object' ? JSON.stringify(msg.message) : msg.message,
      };
      msg.logLevel === 'INFO' ? this.saveInfoQueue(stringifiedMsg) : this.errorQueue.push(stringifiedMsg);
    });
  }

  saveInfoQueue(msg: MessageTrayData) {
    if (!msg.message.toLowerCase().includes(`resourcetype`)) {
      this.infoQueue.push(msg);
    }
  }

  startTimer() {
     this.timerSubscription = timer(0, 120000).subscribe(() => this.flushQueues(false, true));
  }

  flushQueues(info: boolean, error: boolean) {
    if (!this.mTrayService.getUserId() || !this.mTrayService.getCcn()) {
      console.log("Not logged in, do not try to flush message tray");
      return;
    }

    if (this.auditEventInProgress) {
      console.log("AuditEvent in progress, skipping flush");
      return;
    }

    const sendInfo = info && this.infoQueue.length > 0;
    const sendError = error && this.errorQueue.length > 0;

    if (!sendInfo && !sendError) {
      console.log (`No new messages to send to AuditEvent`);
      return;
    }

    this.auditEventInProgress = true;
    const messagesToSend = [
      ...(sendInfo ? this.infoQueue: []),
      ...(sendError ? this.errorQueue: []),
    ];

    if (sendInfo) {
      this.infoQueue = [];
    }
    if (sendError) {
      this.errorQueue = [];
    }

    const payload: AuditData = {
      sessionId: this.sessionId,
      userId: this.mTrayService.getUserId(),
      ccn: this.mTrayService.getCcn(),
      appName: this.appName,
      messageTrayExport: messagesToSend
    };

    const fhirBundle = this.buildFhirAuditBundle(payload);

    this.fhirService.submitAuditEvent(fhirBundle).subscribe({
      next: () => { this.auditEventInProgress = false; },
      error: () => {
        // Re-queue unsent messages (this means, there was an error sending the AuditEvent)
        messagesToSend.forEach(msg => {
          if (msg.logLevel === 'INFO') {
            this.infoQueue.unshift(msg);
          } else {
            this.errorQueue.unshift(msg);
          }
        });

        this.auditEventInProgress = false;
      }
    });
  }

  checkBrowserClose() {
    window.addEventListener('beforeunload', (event) => {
      this.flushQueues(true, true);
    });
  }

  logout() {
    this.flushQueues(true, true);
    console.log(`sent both queues for auditevent generation`);
    this.infoQueue = [];
    this.errorQueue = [];
    sessionStorage.removeItem('auditSessionId');
    this.mTrayService.setUserContext('', '');
    this.mTrayService.deleteFromSessionStorage();
  }

  /**
   * Create the AuditEvent resource - one for INFO, one for ERROR
   * @param auditData
   * @param logLevel
   * @param messages
   */
  buildFhirAuditEvent(auditData: AuditData, logLevel: string, messages: MessageTrayData[]): fhir.AuditEvent {
    const auditEvent: fhir.AuditEvent = {
      resourceType: 'AuditEvent',
      id: uuid.v4(),
      recorded: new Date().toISOString(),
      meta: {
        security: [
          {
            system: 'http://cibmtr.org/codesystem/transplant-center',
            code: "rc_"+auditData.ccn
          }
        ]
      },
      type: {
        system: 'http://terminology.hl7.org/CodeSystem/audit-event-type',
        code: 'rest',
        display: 'RESTful Operation'
      },
      outcome: logLevel.toUpperCase() === 'ERROR' ? '4' : '0', // 4 = minor error, 0 = success (INFO in our case)
      outcomeDesc: logLevel,
      agent: [{
        name: auditData.userId,
        altId: auditData.ccn,
        requestor: true
      }],
      source: {
        site: auditData.appName,
        observer: {
          display: auditData.appName
        },
        extension: [
          {
            url: 'http://cibmtr.org/codesystem/session-id',
            valueString: auditData.sessionId
          }
        ]
      },
      entity: messages.map(msg => ({
        detail: [
          {
            type: 'timestamp',
            valueString: msg.timestamp?.toString() || new Date().toISOString()
          },
          {
            type: 'message',
            valueString: typeof msg.message === 'string' ? msg.message : JSON.stringify(msg.message)
          }
        ]
      }))
    };
    return auditEvent;
  }

  /**
   * Create the transaction bundle containing the AuditEvents
   * @param auditData
   */
  buildFhirAuditBundle(auditData: AuditData): fhir.Bundle {
    const logsByLevel = auditData.messageTrayExport.reduce((acc, log) => {
      const level = log.logLevel;
      if (!acc[level]) acc[level] = [];
      acc[level].push(log);
      return acc;
    }, {} as Record<string, MessageTrayData[]>);

    const bundle: fhir.Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: []
    };

    Object.entries(logsByLevel).forEach(([logLevel, logs]) => {
      const auditEvent = this.buildFhirAuditEvent(auditData, logLevel, logs);
      bundle.entry!.push({
        fullUrl: `urn:uuid:${uuid.v4()}`,
        resource: auditEvent,
        request: {
          method: 'POST',
          url: 'AuditEvent'
        }
      });
    });
    return bundle;
  }

  ngOnDestroy() {
    this.timerSubscription?.unsubscribe();
  }
}
